import LanguagePack from "../../../../../shared/lib/html5-software-library/src/ranj/language/LanguagePack";
import Anchor from "../../../../../shared/lib/html5-software-library/src/ranj/layout/Anchor";
import AbstractScreenMediator from "../../../../../shared/src/ranj/app/ui/AbstractScreenMediator";
import GameEvent from "../../../../../shared/src/ranj/app/core/GameEvent";
import GameModel from "../model/GameModel";
import GameModelEvent from "../model/GameModelEvent";
import GameStatus from "../model/GameStatus";
import PuzzleModelEvent from "../model/PuzzleModelEvent";
import PuzzleStatus from "../model/PuzzleStatus";
import FeedbackPanelView from "./FeedbackPanelView";
import HudView from "./HudView";
import PuzzleView from "./PuzzleView";
import RulesPanelView from "./RulesPanelView";
import { getURLParameter } from "../../../../../shared/lib/html5-software-library/src/ranj/util/URLUtil";
import PopupView from "./PopupView";
import SkinKeys from "../../../../../shared/src/ranj/app/ui/SkinKeys";
import AnimationFactory from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationFactory";
import AnimationOptions from "../../../../../shared/lib/html5-software-library/src/ranj/ui/animation/AnimationOptions";
import AppModel from "../../../../../shared/src/ranj/app/core/AppModel";
import SaveModel from "../model/SaveModel";
import { isNull } from "../../../../../shared/lib/html5-software-library/src/ranj/util/TypeUtil";
import SaveEvent from "../../../../../shared/src/ranj/equalture/savegame/SaveEvent";
import TrackingController from "../model/TrackingController";

export default class GameScreenMediator extends AbstractScreenMediator {

    /**
     * @param {createjs.DisplayObject} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LanguagePack} languagePack 
     * @param {GameModel} gameModel 
     * @param {Object} gameConfig - parsed json (game.json)
     * @param {Object} gameAssets - exported lib with assets
     * @param {SaveModel} saveModel
     * @param {TrackingController} trackingController
     */
    constructor(target, dispatcher, languagePack, layoutManager, soundManager, gameModel, gameConfig, gameAssets, saveModel, trackingController) {
        super(target, dispatcher, languagePack, layoutManager, soundManager);
        this._gameModel = gameModel;
        this._gameAssets = gameAssets;
        this._saveModel = saveModel;
        this._trackingController = trackingController;

        this._gameConfig = gameConfig;
        this._handleTickBound = this._handleTick.bind(this);
    }

    initialize() {
        super.initialize();

        this.stretchBackground(this._target.bg);
        this.anchorObject(this._target.rulesPanel, Anchor.T);
        this.anchorObject(this._target.hud, Anchor.T);
        this.anchorObject(this._target.rowButton, Anchor.B);
        this.anchorObject(this._target.skipButton, Anchor.T);
        this.anchorObject(this._target.feedbackPanel, Anchor.B);

        this._gameModel.addEventListener(GameModelEvent.PUZZLE, this._handleGameModelPuzzle.bind(this));
        this._gameModel.addEventListener(GameModelEvent.ELAPSED_TIME, this._handleGameModelElapsedTime.bind(this));
        this._gameModel.addEventListener(PuzzleModelEvent.MOVE, this._handlePuzzleMove.bind(this));
        this._gameModel.addEventListener(PuzzleModelEvent.MOVE_INVALID, this._handlePuzzleMoveFailed.bind(this));
        this._gameModel.addEventListener(PuzzleModelEvent.STATUS, this._handlePuzzleStatus.bind(this));

        this._hud = new HudView(this._target.hud, this._languagePack);

        this._rulesPanel = new RulesPanelView(this._target.rulesPanel, this._languagePack, this._gameAssets);
        this._rulesPanel.addEventListener(RulesPanelView.CLOSE, this._handleRulesClose.bind(this));
        this._rulesPanel.addEventListener(RulesPanelView.OPEN, this._handleRulesOpen.bind(this));

        this._rowButton = this.createButton(this._target.rowButton, this._handleRowClick.bind(this), this.getText("hud.button.row"));
        this._skipButton = this.createButton(this._target.skipButton, this._handleSkipClick.bind(this), this.getText("hud.button.skip"));

        this._popup = new PopupView(this._target.popup, this._languagePack, this._gameAssets);
        this._popup.close(true);

        this._feedbackPanel = new FeedbackPanelView(this._target.feedbackPanel);

        this._initDebug();

        const obj = this._saveModel.getSaveGame();
        this._restored = !isNull(obj);
        if (this._restored) {
            this._gameModel.applySavedState(obj);
        } else {
            this._startTutorial();
        }
    }

    destroy() {
        super.destroy();
        if (ranj.DEBUG) {
            if (window.gamescreen == this) {
                delete window.gamescreen;
            }
            window.document.removeEventListener('keyup', this._handleKeyUp);
        }
    }

    destroyPuzzle() {
        if (this._puzzleView) {
            this._puzzleView.destroy();
            this._puzzleView = null;
        }
    }

    doNextPuzzle() {
        this._gameModel.doNextPuzzle();
    }

    wrapupOldPuzzle(callback) {
        if (this._puzzleView) {
            const puzzelViewOld = this._puzzleView;
            const delay = puzzelViewOld.animateOut();
            this.callDelayed(callback, delay);
        } else {
            callback();
        }
    }

    initPuzzle() {
        const puzzleModel = this._gameModel.puzzle;
        //Create a new PuzzleView. It will animate itself in.
        this._puzzleView = new PuzzleView(this._target.levelContainer, this._gameAssets, puzzleModel);
        this._feedbackPanel.show(false);
        this._hud.setMoves(this._gameModel.totalMoveCount);
        this._hud.setLevel(this._gameModel.puzzleIndex, this._gameModel.puzzleCount);
        this._updateTime();
        this.setStatus(GameStatus.READY);
        this.initRules();
        if (getURLParameter("skiprules") == "1") {
            this.callDelayed(this.startPuzzle.bind(this), 200);
        } else {
            //Show rules, but give level enough time to animate in first
            this.callDelayed(this.showRules.bind(this), 2400);
        }
    }


    initRules() {
        this._rulesPanel.showRulesForPuzzle(this._gameModel.puzzle.id);
    }

    showRules() {
        this._rulesPanel.open();
        this.callDelayed(this.startPuzzle.bind(this), 2000);
    }
    _handleRulesClose() {
        createjs.Tween.get(this._rowButton.target).to({ alpha: 1 }, 300, createjs.Ease.quadIn);
        if (this._restored) {
            this._restored = false;
            this.callDelayed(() => { this._showFeedback("savegame.restored"); }, 1200);
        }
    }
    _handleRulesOpen() {
        createjs.Tween.get(this._rowButton.target).to({ alpha: 0 }, 300, createjs.Ease.quadOut);
    }

    setStatus(value) {
        this._status = value;
        this._rowButton.setVisible(this._status == GameStatus.RUNNING || this._status == GameStatus.TIMEOUT);
        this._skipButton.setVisible(this._status == GameStatus.TIMEOUT);
    }

    startPuzzle() {
        this._hud.setMoves(this._getMoveCount());
        this._gameModel.startPuzzle();
        this._feedbackPanel.show(false);
        this._lastTick = Date.now();
        this.setStatus(GameStatus.RUNNING);
        this.startTicker();
    }

    doNextAttempt() {
        if (this._gameModel.failedAttempts >= 1) {
            this._gameModel.resetPuzzle();
        }
        this.startPuzzle();
    }

    startTicker() {
        this._target.stage.removeEventListener("tick", this._handleTickBound);
        this._target.stage.addEventListener("tick", this._handleTickBound);
    }
    stopTicker() {
        this._target.stage.removeEventListener("tick", this._handleTickBound);
    }

    _handleTick() {
        const now = Date.now();
        if (!this._paused) {
            this.step(now - this._lastTick);
        }
        this._lastTick = now;
    }

    step(dt) {
        switch (this._status) {
            case GameStatus.RUNNING:
            case GameStatus.PUZZLE_FAILED:
            case GameStatus.TIMEOUT:
                this._gameModel.addElapsedTime(dt);
                break;
        }
    }

    _updateTime() {
        this._hud.setTime(this._gameModel.elapsedTime);
    }
    
    _handleRowClick() {
        if (this._status == GameStatus.RUNNING || this._status == GameStatus.TIMEOUT) {
            this._gameModel.puzzle.crossRiver();
        }
    }

    _handleSkipClick() {        
        if (this._status != GameStatus.TIMEOUT) {
            return;
        }
        //show confirmation popup
        const allComplete = !this._gameModel.hasNextPuzzle();
        const id = allComplete ? "puzzleSkipLast" : "puzzleSkip";
        const title = this.getText(id + ".title");
        const body = this.getText(id + ".body");
        const buttonLabel = this.getText(id + ".button1");
        const buttonLabel2 = this.getText(id + ".button2");
        const buttonParams = [
            { label: buttonLabel, callback: this._skip.bind(this) },
            { label: buttonLabel2 }
        ];
        this._showPopup(title, body, buttonParams, PopupView.GENERIC);
    }

    _skip() {
        this._trackingController.trackSkip(this._puzzleIndex, this._gameModel.elapsedTime);
        const allComplete = !this._gameModel.hasNextPuzzle();
        if (allComplete) {
            this.gotoScreen(SkinKeys.END_SCREEN_SKIN);
        } else {
            this.doNextPuzzle();
        }
    }

    _handleGameModelPuzzle({ data: puzzleModel }) {
        this.wrapupOldPuzzle(this.initPuzzle.bind(this));
    }

    _handleGameModelElapsedTime({ data: elpasedTime }) {
        this._updateTime();
    }

    _handlePuzzleMove(e) {
        this._hud.setMoves(this._getMoveCount());
    }

    _handlePuzzleMoveFailed({ data }) {
        const { failedConditions, actor, location } = data;
        this._showFeedback("feedback." + failedConditions[0].id);
        this._puzzleView.onMoveInvalid(actor, location);
    }

    _handlePuzzleStatus({ data: status }) {
        let enabled = false;
        switch (status) {            
            case PuzzleStatus.STARTED:
                enabled = true;
                break;
            case PuzzleStatus.FAILED:
                this._onPuzzleFailed();
                break;
            case PuzzleStatus.COMPLETED:
                this._onPuzzleCompleted();
                break;
            case PuzzleStatus.TIMEOUT:
                this._onPuzzleTimeout();
                enabled = true;
                break;
        }
        this._rowButton.setEnabled(enabled);
        this._rulesPanel.enabled = enabled;
    }

    _onPuzzleFailed() {
        //first show non-textual feedback
        const failed = this._gameModel.failedConstraints;
        //If we take the debug shortcut in levels that cant fail, failed.length == 0
        if (failed.length == 0) {
            failed.push({ id: "failed", actors: [] });
        }
        const failure = failed[0];

        const actorIds = failure.actors;
        //TODO Fix: apply law of demeter in retrieving actors
        const actorParams = this._gameModel.puzzle.config.actors.filter(({ id }) => actorIds.indexOf(id) > -1);

        this.callDelayed(() => {
            const title = this.getText("puzzleFailed.title");
            const body = this.getText("feedback." + failure.id);
            const buttonLabel = this.getText("puzzleFailed.button");
            const buttonParams = [{ label: buttonLabel, callback: this.doNextAttempt.bind(this) }];
            this._showPopup(title, body, buttonParams, PopupView.FAILED);
            this._popup.setActors(actorParams);
            this.setStatus(GameStatus.PUZZLE_FAILED);
        }, 1600);
    }

    _onPuzzleCompleted() {
        //Hurray! Next puzzle...
        this.stopTicker();
        const allComplete = !this._gameModel.hasNextPuzzle();
        let id, callback;
        if (allComplete) {
            id = "allPuzzlesCompleted";
            callback = () => { this.gotoScreen(SkinKeys.END_SCREEN_SKIN); }
        } else {
            id = "puzzleCompleted";
            callback = this.doNextPuzzle.bind(this);
        }
        this.callDelayed(() => {
            const title = this.getText(id + ".title");
            const body = this.getText(id + ".body");
            const buttonLabel = this.getText(id + ".button");
            const buttonParams = [{ label: buttonLabel, callback: callback }];
            this._showPopup(title, body, buttonParams, PopupView.COMPLETED);
            this.setStatus(allComplete ? GameStatus.FINISHED : GameStatus.PUZZLE_COMPLETED);
        }, 700);
    }

    _onPuzzleTimeout() {
        this.setStatus(GameStatus.TIMEOUT);
        const scale = this._skipButton.target.scaleX * 1.3;
        createjs.Tween.get(this._skipButton.target, { loop: 1, bounce: 1 }).to({ scaleX: scale, scaleY: scale}, 150);
    }



    _showFeedback(id, disappear = true) {
        const body = this.getText(id);
        this._feedbackPanel.setText(body, disappear);
    }


    _showPopup(title, body, buttonParams, type) {
        this._rulesPanel.close(true);
        this._rulesPanel.enabled = false;
        this._popup.setContent(title, body, buttonParams, type);
        this._popup.open();
    }


    _getMoveCount() {
        return this._gameModel.totalMoveCount;
    }


    //--------------------
    //   TUTORIAL
    //--------------------


    _startTutorial() {
        if (getURLParameter("skiptutorial") != "1") {
            this.destroyPuzzle();
            this._puzzleView = new PuzzleView(this._target.levelContainer, this._gameAssets, this._gameModel.initTutorialPuzzle());
            this._feedbackPanel.show(false);
            this._hud.setMoves(0);
            this._hud.setLevel(0, this._gameModel.puzzleCount);
            this._updateTime();
            this.setStatus(GameStatus.TUTORIAL);
            this._rowButton.setVisible(true);
            this._rowButton.target.alpha = 0;
            createjs.Tween.get(this._rowButton.target).wait(500).to({ alpha: 1 }, 1000, createjs.Ease.quadInOut);
            this._skipButton.setVisible(true);
            createjs.Tween.get(this._skipButton.target).wait(500).to({ alpha: 1 }, 1000, createjs.Ease.quadInOut);
            const delay = this._gameConfig.getValue("tutorial.delay") + PuzzleView.ANIMATE_IN_DURATION;
            const duration = this._gameConfig.getValue("tutorial.fadeDuration");
            const options = new AnimationOptions(delay, duration, createjs.Ease.quadIn);
            this._rulesPanel.enabled = false;
            this.openPopup(SkinKeys.TUTORIAL_OVERLAY_SKIN, this._handleTutorialClose.bind(this), AnimationFactory.FADE_IN, options);
        } else {
            this._startAfterTutorial();
        }
    }

    _handleTutorialClose() {
        this._rulesPanel.enabled = true;
        this.callDelayed(this._startAfterTutorial.bind(this), 1000);
    }

    _startAfterTutorial() {
        this.doNextPuzzle();
    }

    //--------------------
    //   DEBUG STUFF
    //--------------------


    _initDebug() {
        if (ranj.DEBUG) {
            this._handleKeyUp = this._handleKeyUp.bind(this);
            window.document.addEventListener('keyup', this._handleKeyUp);
            //url parameter can indicate level, starting at 1 for first level
            //  or negative: -1 means last puzzle etc
            let level = parseInt(getURLParameter("level"));
            if (!isNaN(level)) {
                if (level < 0) {
                    level = this._gameModel.puzzleCount + level + 1;
                }
                this._gameModel._puzzleIndex = level - 2;
            }

            //TEMP/TEST:
            window.gamescreen = this;
        }
    }
    _handleKeyUp(e) {
        const c = String.fromCharCode(e.which);
        if (c == "C") this._fakeCompletion();
        else if (c == "F") this._fakeFailure();
        else if (c == "T") this._fakeTimout();
        else if (c == "I") this._startTutorial();
        else if (c == "N") this.doNextPuzzle();
        else if (c == "E") {
            AppModel.easterEggsEnabled = !AppModel.easterEggsEnabled;
            this._jumpToPuzzle(this._gameModel._puzzleIndex);
        }
        else if (c == "P") this._paused = !this._paused;
        else if (c == "0") this._startTutorial();
        else if (c >= "1" && c <= this._gameModel.puzzleCount) this._jumpToPuzzle(parseInt(c) - 1);
    }

    _jumpToPuzzle(idx) {
        this._gameModel._puzzleIndex = idx - 1;
        this.doNextPuzzle();
    }
    _fakeCompletion() {
        this._gameModel.puzzle.complete();
    }
    _fakeFailure() {
        this._gameModel.puzzle.fail();
    }
    _fakeTimout() {
        this._gameModel.addElapsedTime(this._gameModel.remainingPuzzleTime - 1);
    }

}

